import { useEffect, useRef, useState } from "react";
import { Form, required, useLogin, useNotify, useTranslate } from "ra-core";
import type { SubmitHandler, FieldValues } from "react-hook-form";
import { Link, useLocation, useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import { TextInput } from "@/components/admin/text-input";
import { getAuthErrorKey } from "./authHelpers";
import {
  AuthShell,
  authButtonClassName,
  authInputClassName,
} from "./AuthShell";
import { SSOAuthButton } from "./SSOAuthButton";
import {
  disableEmailPasswordAuthentication,
  googleWorkplaceDomain,
  signupEnabled,
} from "./authConfig";

/**
 * Login page displayed when authentication is enabled and the user is not authenticated.
 *
 * Automatically shown when an unauthenticated user tries to access a protected route.
 * Handles login via authProvider.login() and displays error notifications on failure.
 *
 * @see {@link https://marmelab.com/shadcn-admin-kit/docs/loginpage LoginPage documentation}
 * @see {@link https://marmelab.com/shadcn-admin-kit/docs/security Security documentation}
 */
export const LoginPage = (props: { redirectTo?: string }) => {
  const { redirectTo } = props;
  const [loading, setLoading] = useState(false);
  const hasDisplayedRecoveryNotification = useRef(false);
  const location = useLocation();
  const navigate = useNavigate();
  const login = useLogin();
  const notify = useNotify();
  const translate = useTranslate();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const shouldNotify = searchParams.get("passwordRecoveryEmailSent") === "1";

    if (!shouldNotify || hasDisplayedRecoveryNotification.current) {
      return;
    }

    hasDisplayedRecoveryNotification.current = true;
    notify("crm.auth.recovery_email_sent", {
      type: "success",
      messageArgs: {
        _: "If you're a registered user, you should receive a password recovery email shortly.",
      },
    });

    searchParams.delete("passwordRecoveryEmailSent");
    const nextSearch = searchParams.toString();
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : "",
      },
      { replace: true },
    );
  }, [location.pathname, location.search, navigate, notify]);

  const handleSubmit: SubmitHandler<FieldValues> = (values) => {
    setLoading(true);
    login(values, redirectTo)
      .then(() => {
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        notify(getAuthErrorKey(error), { type: "error" });
      });
  };

  return (
    <AuthShell>
      <div className="space-y-6">
        <div>
          <h2 className="font-heading text-2xl font-extrabold tracking-tight">
            {translate("crm.auth.login.title", { _: "Welcome back" })}
          </h2>
          <p className="mt-1 text-[13px] text-muted-foreground">
            {translate("crm.auth.login.subtitle", {
              _: "Log in to continue in PalmTree Hub",
            })}
          </p>
        </div>
        {disableEmailPasswordAuthentication ? null : (
          <Form
            className="space-y-4 [&_[data-slot=form-label]]:text-foreground"
            onSubmit={handleSubmit}
          >
            <TextInput
              label="ra.auth.email"
              source="email"
              type="email"
              autoComplete="email"
              placeholder={translate("crm.auth.placeholders.email", {
                _: "naam@bedrijf.nl",
              })}
              inputClassName={authInputClassName}
              validate={required()}
            />
            <div>
              <TextInput
                label="ra.auth.password"
                source="password"
                type="password"
                autoComplete="current-password"
                inputClassName={authInputClassName}
                validate={required()}
              />
              <div className="-mt-1 flex justify-end">
                <Link
                  to="/forgot-password"
                  className="text-[13px] font-medium text-link hover:underline"
                >
                  {translate("ra-supabase.auth.forgot_password", {
                    _: "Forgot password?",
                  })}
                </Link>
              </div>
            </div>
            <Button
              type="submit"
              className={authButtonClassName}
              disabled={loading}
            >
              {translate("ra.auth.sign_in")}
            </Button>
          </Form>
        )}
        {googleWorkplaceDomain ? (
          <SSOAuthButton className="w-full" domain={googleWorkplaceDomain}>
            {translate("crm.auth.sign_in_google_workspace", {
              _: "Sign in with Google Workplace",
            })}
          </SSOAuthButton>
        ) : null}
        {disableEmailPasswordAuthentication || !signupEnabled ? null : (
          <p className="text-center text-[13px] text-muted-foreground">
            {translate("crm.auth.login.no_account", {
              _: "Don't have an account?",
            })}{" "}
            <Link
              to="/sign-up"
              className="font-semibold text-link hover:underline"
            >
              {translate("crm.auth.signup.create_account", {
                _: "Create account",
              })}
            </Link>
          </p>
        )}
      </div>
    </AuthShell>
  );
};
